/**
 * `forms.contact` — data schemas. Fixed contact capability, NOT a form
 * builder. Public content = bounded copy fields. Private delivery config
 * (recipient, origins, Turnstile policy, retention) is SERVER-SIDE ONLY —
 * deliberately not part of module data, never readable by editing surfaces
 * or agents.
 */
import { z } from 'zod';
import { MAX_FORM_ID_TOKEN_LENGTH } from '../../forms/form-id.js';

export const FormsContactData = z.object({
  heading: z.string().min(1).max(120),
  intro: z.string().max(400).optional(),
  submitLabel: z.string().min(1).max(60),
  successMessage: z.string().min(1).max(240),
  errorMessage: z.string().min(1).max(240),
  consentText: z.string().max(400).optional(),
});

export const FormsContactConfig = z.object({
  /** Opaque signed form identifier; recipient resolves SERVER-SIDE from it.
   *  The pipeline sets a per-site value; no shared default (a shared id would
   *  cross-route submissions). Bounded by the signed-token ceiling
   *  (MAX_FORM_ID_TOKEN_LENGTH), NOT 120: a real minted token for a legal
   *  siteId (<=40) + instanceId (<=80) is ~206 chars, so a 120 cap would
   *  reject valid tokens and break the E render hand-off. */
  formId: z.string().min(8).max(MAX_FORM_ID_TOKEN_LENGTH),
  /** Same-origin POST path on the shared submission service (single slash). */
  submitPath: z.string().regex(/^\/(?!\/)[a-z0-9/-]*$/).default('/api/forms/contact'),
});

export type FormsContactDataT = z.infer<typeof FormsContactData>;
export type FormsContactConfigT = z.infer<typeof FormsContactConfig>;
